import React, { useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Input } from 'syngenta-digital-cropwise-react-ui-kit';

import SearchIcon from 'assets/search.svg';
import CollapsibleOption from 'components/CollapsibleOption';
import { Context } from 'components/Store';
import UiKitButton from 'components/UiKit/Button';
import { ArrowLeft, ArrowRight } from 'routes/icons';
import { ReportsContext } from 'screens/Reports/context/ReportsProvider';
import useReportMetadata, {
  getReportByName,
  getReportsByCategory
} from 'screens/Reports/hooks/useReportMetadata';
import featureFlagRenderItem from 'helpers/featureFlagRenderItem';
import { useFormikContext } from 'formik';

const buttonStyle = {
  position: 'fixed',
  border: '1px solid #0092E4',
  borderRadius: '50%',
  height: '24px',
  width: '24px'
};

const SideMenu = ({ onChange }) => {
  const { values } = useFormikContext();
  const {
    toggleNavbar,
    setActiveReport,
    getActiveReport,
    setFormSelections,
    state: { navbarOpen }
  } = useContext(ReportsContext);
  const [
    {
      organization: { id: orgId }
    }
  ] = useContext(Context);
  const activeReport = getActiveReport();
  const reports = useReportMetadata();

  const [openSideMenuClasses, setOpenSideMenu] = useState('w-0');
  const [opacity, setOpacity] = useState('opacity-0');
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    setOpenSideMenu(navbarOpen ? 'w-60' : 'w-5');
    setOpacity(navbarOpen ? 'opacity-100' : 'opacity-0');
  }, [navbarOpen]);

  const resetSelectedItems = () => {
    setSelectedItem(null);
  };

  const handleSelectedItem = reportName => {
    const selectedReport = getReportByName(reports, reportName);
    if (
      activeReport &&
      activeReport.category === selectedReport.category &&
      activeReport.subType === selectedReport.subType
    ) {
      return;
    }
    const newFormSelections = {};
    if (values.selectedCropSeasons) {
      newFormSelections.cropSeasons = values.selectedCropSeasons;
    }
    if (values.cropSeasonStartDate) {
      newFormSelections.startDate = values.cropSeasonStartDate;
    }
    if (values.cropSeasonEndDate) {
      newFormSelections.endDate = values.cropSeasonEndDate;
    }
    if (values.checkedProperties) {
      newFormSelections.properties = values.checkedProperties;
    }
    if (values.checkedProducts) {
      newFormSelections.products = values.checkedProducts;
    }

    setFormSelections(newFormSelections);
    onChange();
    resetSelectedItems();
    setSelectedItem(reportName);
    setActiveReport(selectedReport);
  };

  const reportsByCategories = reports.reduce((accum, report) => {
    if (!accum[report.category]) {
      return {
        ...accum,
        [report.category]: getReportsByCategory(
          reports,
          report.category
        ).filter(_report =>
          featureFlagRenderItem(
            _report.featureFlag,
            _report.featureFlagByOrg,
            orgId
          )
        )
      };
    }
    return accum;
  }, {});

  const reportMenuOptions = Object.keys(reportsByCategories)
    .filter(category => reportsByCategories[category].length)
    .sort((a, b) => a.localeCompare(b))
    .map(category => (
      <CollapsibleOption
        key={category}
        title={category}
        options={reportsByCategories[category].map(report => ({
          key: report.name,
          label: report.subType.toUpperCase()
        }))}
        selectedIdx={selectedItem}
        handleSelectedItem={id => handleSelectedItem(id)}
        showOptions
      />
    ));

  const arrowIcon = useMemo(() => (navbarOpen ? ArrowLeft : ArrowRight), [
    navbarOpen
  ]);

  return (
    <div
      className={`${openSideMenuClasses} sticky top-0 transition-width duration-200 overflow-x-hidden overflow-y-auto report-details-scrollbar`}
      style={{
        backgroundColor: '#363948'
      }}
      data-testid="sidemenu-container"
    >
      <div className="w-full h-12 relative">
        <div className="absolute top-3 right-3">
          <UiKitButton
            data-testid="toggle-navbar-button"
            aria-label={
              navbarOpen
                ? 'Collapse reports navigation'
                : 'Expand reports navigation'
            }
            icon={arrowIcon}
            style={buttonStyle}
            onClick={toggleNavbar}
          />
        </div>
      </div>
      <div
        className={`transition-opacity duration-200 w-60 ${opacity}`}
        data-testid="sidemenu-content"
      >
        <h3 className="text-xl text-center mt-3 text-white">
          Reports Navigation
        </h3>
        <div className="mr-5 ml-5 mt-5" style={{ display: 'none' }}>
          <Input
            prefix={<img alt="Search icon" src={SearchIcon} />}
            allowClear
            autoComplete="off"
            placeholder="Search"
          />
        </div>
        <div className="mt-8">{reportMenuOptions}</div>
      </div>
    </div>
  );
};

SideMenu.propTypes = {
  onChange: PropTypes.func.isRequired
};

export default SideMenu;
